// src/components/ScrollToTopButton.jsx

import React, { useState, useEffect } from 'react'
import { ArrowUp } from 'lucide-react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    const toggleVisibility = () => {
      const scrollTop = window.scrollY
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight

      setVisible(scrollTop > 250)

      const progress =
        scrollHeight > 0 ? Math.min((scrollTop / scrollHeight) * 100, 100) : 0

      setScrollProgress(progress)
    }

    toggleVisibility()

    window.addEventListener('scroll', toggleVisibility, { passive: true })

    return () => {
      window.removeEventListener('scroll', toggleVisibility)
    }
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: shouldReduceMotion ? 'instant' : 'smooth'
    })
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type='button'
          onClick={scrollToTop}
          aria-label='Scroll to top'
          title='Back to top'
          initial={
            shouldReduceMotion ? false : { opacity: 0, y: 40, scale: 0.8 }
          }
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={
            shouldReduceMotion
              ? { opacity: 0 }
              : { opacity: 0, y: 30, scale: 0.8 }
          }
          transition={{
            duration: shouldReduceMotion ? 0 : 0.3,
            ease: 'easeOut'
          }}
          whileHover={shouldReduceMotion ? {} : { scale: 1.1, rotate: 5 }}
          whileTap={shouldReduceMotion ? {} : { scale: 0.92 }}
          className='fixed bottom-[190px] right-4 sm:bottom-[225px] sm:right-7 z-[9996] w-[42px] h-[42px] sm:w-[52px] sm:h-[52px] rounded-full bg-gradient-to-br from-[#211f57] to-[#07529A] text-[#FFF44F] shadow-[0_8px_25px_rgba(33,31,87,0.30)] flex items-center justify-center transition-shadow duration-300 hover:shadow-xl'
        >
          {/* Circular Progress */}
          <svg
            className='absolute inset-0 w-full h-full -rotate-90 pointer-events-none'
            viewBox='0 0 52 52'
            aria-hidden='true'
          >
            <circle
              cx='26'
              cy='26'
              r='23'
              fill='none'
              stroke='rgba(255,255,255,0.2)'
              strokeWidth='2'
            />

            <circle
              cx='26'
              cy='26'
              r='23'
              fill='none'
              stroke='#FFF44F'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeDasharray={2 * Math.PI * 23}
              strokeDashoffset={2 * Math.PI * 23 * (1 - scrollProgress / 100)}
              className='transition-all duration-150'
            />
          </svg>

          <ArrowUp
            size={19}
            strokeWidth={2.8}
            className='relative z-10 sm:w-[22px] sm:h-[22px]'
          />
        </motion.button>
      )}
    </AnimatePresence>
  )
}

export default ScrollToTopButton
